import {
    ColumnDef,
    ColumnFiltersState,
    RowSelectionState,
    SortingState,
    Updater,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table"
import { ArrowUpDown, ChevronDown, Search } from "lucide-react"
import * as React from "react"

import { Button } from "./button"
import { Checkbox } from "./checkbox"
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuTrigger } from "./dropdown-menu"
import { Input } from "./input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./table"

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  searchKey?: string
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  loading?: boolean
  emptyMessage?: string
  toolbarContentRight?: React.ReactNode
  toolbarContentLeft?: React.ReactNode
  showColumnToggle?: boolean

  // Server-side pagination props
  serverSide?: boolean
  pagination?: {
    total: number
    page: number
    limit: number
    pages: number
  }
  onPaginationChange?: (page: number, limit: number) => void
  onSortingChange?: (sortBy: string, sortOrder: 'ASC' | 'DESC') => void

  enableRowSelection?: boolean
  rowSelection?: RowSelectionState
  onRowSelectionChange?: (selection: RowSelectionState) => void
  getRowId?: (row: TData) => string
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchKey,
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  loading = false,
  emptyMessage = "No results found.",
  toolbarContentRight,
  toolbarContentLeft,
  showColumnToggle = true,
  serverSide = false,
  pagination: serverPagination,
  onPaginationChange,
  onSortingChange,
  enableRowSelection = false,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  getRowId,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({})
  const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({})

  const rowSelection = controlledRowSelection ?? internalRowSelection
  const handleRowSelectionChange = React.useCallback(
    (updater: Updater<RowSelectionState>) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater
      if (onRowSelectionChange) onRowSelectionChange(next)
      else setInternalRowSelection(next)
    },
    [rowSelection, onRowSelectionChange],
  )

  const selectionColumn = React.useMemo<ColumnDef<TData, TValue>>(
    () => ({
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all on page"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    }),
    [],
  )

  const tableColumns = React.useMemo(
    () => (enableRowSelection ? [selectionColumn, ...columns] : columns),
    [enableRowSelection, selectionColumn, columns],
  )

  // Handle sorting changes for server-side mode
  React.useEffect(() => {
    if (serverSide && onSortingChange && sorting.length > 0) {
      const sort = sorting[0]
      const sortOrder = sort.desc ? 'DESC' : 'ASC'
      onSortingChange(sort.id, sortOrder)
    }
  }, [sorting, serverSide, onSortingChange])

  const table = useReactTable({
    data,
    columns: tableColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: serverSide ? undefined : getPaginationRowModel(),
    getSortedRowModel: serverSide ? undefined : getSortedRowModel(),
    getFilteredRowModel: serverSide ? undefined : getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: handleRowSelectionChange,
    enableRowSelection,
    ...(getRowId ? { getRowId: (row: TData) => getRowId(row) } : {}),
    manualPagination: serverSide,
    manualSorting: serverSide,
    manualFiltering: serverSide,
    ...(serverSide && serverPagination ? { pageCount: serverPagination.pages } : {}),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  })

  const colSpan = table.getVisibleLeafColumns().length
  const showToolbar = Boolean(searchKey || toolbarContentLeft || toolbarContentRight || showColumnToggle)

  return (
    <div className="w-full">
      {showToolbar && <div className="flex items-center py-4 gap-4">
        {searchKey && (
          <div className="relative max-w-sm">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            {serverSide && onSearchChange ? (
              <Input
                placeholder={searchPlaceholder}
                value={searchValue ?? ""}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-8"
              />
            ) : (
              <Input
                placeholder={searchPlaceholder}
                value={(table.getColumn(searchKey)?.getFilterValue() as string) ?? ""}
                onChange={(event) =>
                  table.getColumn(searchKey)?.setFilterValue(event.target.value)
                }
                className="pl-8"
              />
            )}
          </div>
        )}
        {toolbarContentLeft}
        <div className="ml-auto flex items-center gap-2">
          {toolbarContentRight}
          {showColumnToggle && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  // Format column name: replace underscores with spaces and capitalize
                  const displayName = column.id
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());

                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {displayName}
                    </DropdownMenuCheckboxItem>
                  )
                })}
            </DropdownMenuContent>
          </DropdownMenu>
          )}
        </div>
      </div>}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : (
                        <div
                          className={
                            header.column.getCanSort()
                              ? 'flex items-center gap-1 cursor-pointer select-none'
                              : ''
                          }
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <ArrowUpDown className="h-4 w-4" />
                          )}
                        </div>
                      )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="h-24 text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={colSpan} className="h-24 text-center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between space-x-2 py-4">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${serverSide && serverPagination ? serverPagination.limit : (table.getState().pagination?.pageSize || 10)}`}
            onValueChange={(value) => {
              const newLimit = Number(value)
              if (serverSide && onPaginationChange) {
                onPaginationChange(1, newLimit) // Reset to page 1 when changing limit
              } else {
                table.setPageSize(newLimit)
              }
            }}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={serverSide && serverPagination ? serverPagination.limit : (table.getState().pagination?.pageSize || 10)} />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 20, 30, 40, 50].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">
              Page {serverSide && serverPagination ? serverPagination.page : ((table.getState().pagination?.pageIndex || 0) + 1)} of{" "}
              {serverSide && serverPagination ? serverPagination.pages : table.getPageCount()}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => {
                if (serverSide && onPaginationChange) {
                  onPaginationChange(1, serverPagination?.limit || 25)
                } else {
                  table.setPageIndex(0)
                }
              }}
              disabled={serverSide ? (serverPagination?.page === 1) : !table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to first page</span>
              {'<<'}
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (serverSide && onPaginationChange) {
                  onPaginationChange((serverPagination?.page || 1) - 1, serverPagination?.limit || 25)
                } else {
                  table.previousPage()
                }
              }}
              disabled={serverSide ? (serverPagination?.page === 1) : !table.getCanPreviousPage()}
            >
              <span className="sr-only">Go to previous page</span>
              {'<'}
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => {
                if (serverSide && onPaginationChange) {
                  onPaginationChange((serverPagination?.page || 1) + 1, serverPagination?.limit || 25)
                } else {
                  table.nextPage()
                }
              }}
              disabled={serverSide ? (serverPagination?.page || 1) >= (serverPagination?.pages || 1) : !table.getCanNextPage()}
            >
              <span className="sr-only">Go to next page</span>
              {'>'}
            </Button>
            <Button
              variant="outline"
              className="hidden h-8 w-8 p-0 lg:flex"
              onClick={() => {
                if (serverSide && onPaginationChange) {
                  onPaginationChange(serverPagination?.pages || 1, serverPagination?.limit || 25)
                } else {
                  table.setPageIndex(table.getPageCount() - 1)
                }
              }}
              disabled={serverSide ? (serverPagination?.page || 1) >= (serverPagination?.pages || 1) : !table.getCanNextPage()}
            >
              <span className="sr-only">Go to last page</span>
              {'>>'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
